import { RestaurantCapability } from "src/business/account/capability";
import { Asset } from "./gallery/asset";
import { Dayjs } from "dayjs";

export interface HourType {
  day: string;
  morning: HourObjectType;
  evening: HourObjectType | null;
}

export interface HourObjectType {
  start: Dayjs;
  end: Dayjs;
}

export type DayHoursList = HourType[];
export type Price = "$" | "$$" | "$$$" | "$$$$";

export interface RestaurantType {
  id: string;
  name: string;
  city: string;
  quartier: string;
  price: Price;
  phone: string;
  address: string;
  bookingDuration: number;
  paymentMethods: PaymentMethod;
  numberOfSeats: number;
  isLive: boolean;
  servesAlcohol: boolean;
  featured: boolean;
  googleRating: number;
  hasAnimation: boolean;
  instaTag: string;
  cover: Asset;
  tags: string[];
  capabilities: RestaurantCapability[];
  organizationId: string | null;
  /**
   * Whether the restaurant's subscription expiration has passed — it is still
   * listed because the 3-day grace period has not run out yet. Omitted for
   * admins, who are not subject to the subscription gate.
   */
  subscriptionExpired?: boolean;
}

export enum PaymentMethod {
  Cash = "Cache",
  Tpe = "TPE",
}

/**
 * What every restaurant read model carries. Read from a per-page query rather
 * than from the shared list row, so that saving invalidates the query the page
 * owns instead of the list every screen shares.
 */
export type RestaurantDetails = {
  schedule: DayHoursList;
  leaves: string[];
  // Map pin.
  latitude: number;
  longitude: number;
  // Total number of bookable tables. `null` until the restaurateur configures it,
  // which gates access to the table-schedule board.
  numberOfTables: number | null;
  // Largest party the restaurant accepts on a single booking. `null` when it
  // sets no limit, which leaves the public booking page uncapped.
  maximumPartySize: number | null;
};

/**
 * The restaurant-information page's read model: the base plus the profile it
 * edits. The list row still carries these fields for the surfaces that only
 * need a label (sidebar picker, dashboard header, admin tables) — this is the
 * copy the page edits against.
 */
export type RestaurantInfoDetails = RestaurantDetails & {
  name: string;
  phone: string;
  address: string;
  quartier: string;
  city: string;
  paymentMethods: PaymentMethod;
  numberOfSeats: number;
  servesAlcohol: boolean;
  hasAnimation: boolean;
  instaTag: string;
  tags: string[];
};

/**
 * The read model behind the two schedule-facing pages. `bookingDuration` sizes
 * a slot on the table-schedule board and is the value the opening-hours page
 * edits; both read it here rather than from the list row.
 */
export type RestaurantScheduleDetails = RestaurantDetails & {
  bookingDuration: number;
};

export type CityInformation = {
  city: string;
  lat: string;
  lng: string;
  country: string;
  iso2: string;
  admin_name: string;
  capital: string;
  population: string;
  population_proper: string;
};
